import {
  normalizeEvents,
  findEventToKeep,
  collectOpenEventRecords,
  matchEventsToReopen,
} from './helpers/leaderboardV2_helper';
import { fetchResultScope, clearEventSlice, fullClearEventSlice } from './event_index';
import { clearSummaries, closePurseSummaries, fetchSummary } from './summaries_index';
import { selectOpenSummaryKey, selectEnabledSummaryKeys } from '../selectors';
import { nextPollInterval, shouldContinuePolling } from '../utils/poll_utils';
import { buildRoundsUrl } from '../utils/rounds_url';
import { VIEW_MODES } from '../constants';
import { componentText } from '../utils/i18n_utils';

export const ActionTypes = {
  FETCH_EVENTS_REQUEST: 'leaderboardV2/FETCH_EVENTS_REQUEST',
  FETCH_EVENTS_SUCCESS: 'leaderboardV2/FETCH_EVENTS_SUCCESS',
  FETCH_EVENTS_FAILURE: 'leaderboardV2/FETCH_EVENTS_FAILURE',
  SET_ROUND_ID: 'leaderboardV2/SET_ROUND_ID',
  SET_ROUND_ONGOING: 'leaderboardV2/SET_ROUND_ONGOING',
  SET_ROUND_LIST: 'leaderboardV2/SET_ROUND_LIST',
  COMMIT_EVENTS: 'leaderboardV2/COMMIT_EVENTS',
  SET_ORIENTATION_LOCKED: 'leaderboardV2/SET_ORIENTATION_LOCKED',
};

// Whether the page is rotated to landscape (fullscreen orientation lock, or
// the native app bridge). Page-level state in the store on purpose: the
// orientation button mounts inside the selected event tile, so keeping this
// in component state made every event switch reset it to false while still
// rotated - mislabeling the button and, on the native bridge's blind toggle,
// rotating an already-landscape app back to portrait.
export const setOrientationLocked = (locked) => ({
  type: ActionTypes.SET_ORIENTATION_LOCKED,
  payload: locked,
});

// The tournaments response body carries the round's polling-eligibility flag
// (round_ongoing). It is part of the ETag, so any flip arrives as a fresh 200;
// a 304 means the flag is unchanged and the stored value still holds (nothing
// to dispatch). Keyed by the fetched roundId so a slow response can't mislabel
// another round; dispatched only on a value change to avoid no-op store writes.
const dispatchRoundOngoing = (dispatch, getState, roundId, roundOngoing) => {
  if (typeof roundOngoing !== 'boolean') return;

  const round = getState().leaderboardV2.roundList?.find((r) => String(r.id) === String(roundId));
  if (!round || round.ongoing === roundOngoing) return;

  dispatch({ type: ActionTypes.SET_ROUND_ONGOING, payload: { roundId, ongoing: roundOngoing } });
};

// The round list is otherwise static per page load - desktop users reload the
// page to see rounds that started or had results released afterwards, but the
// mobile app cannot reload, so the mobile rounds modal refetches the list on
// open - and the desktop round dropdown does the same, so a round that appears
// while the page is open is one click away (a single-round page shows no
// dropdown, so it keeps needing the reload - accepted).
// Customized pages refetch through the same endpoint with the widget's page_id
// (utils/rounds_url): the server rebuilds the page's own map, so the entries
// come back with their event_ids and every later fetch keeps the page's
// tournament filter. With no page id in the document's query string the
// stored list is kept.
//
// Like fetchPreviewEvents, the request state is the caller's, not the store's:
// the result is a discriminated status - 'ok' (list current), 'skipped'
// (nothing to fetch), 'error' - and each picker keeps its own loading/error
// state around the await. The stored list is kept on failure.
//
// Conditional like the list poller: the last response's ETag (a digest of the
// server's rendered list) rides along as If-None-Match, and a 304 means the
// stored list is still what the server would send - nothing to dispatch, no
// re-render, still 'ok' for the picker.
export const fetchRounds = () => async (dispatch, getState) => {
  const { roundsUrl, roundList, roundListEtag } = getState().leaderboardV2;
  const url = buildRoundsUrl(roundsUrl, roundList);
  if (!url) return { status: 'skipped' };

  try {
    const headers = {};
    if (roundListEtag) headers['If-None-Match'] = roundListEtag;

    const res = await fetch(url, { headers });

    if (res.status === 304) return { status: 'ok' };
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const data = await res.json();
    if (!Array.isArray(data.rounds)) throw new Error('Malformed rounds payload');

    dispatch({
      type: ActionTypes.SET_ROUND_LIST,
      payload: { rounds: data.rounds, etag: res.headers.get('ETag') },
    });
    return { status: 'ok' };
  } catch {
    return { status: 'error' };
  }
};

let snapshotController = null;

const buildTournamentsUrl = (tournamentsUrl, roundId, roundList = []) => {
  const base = tournamentsUrl.replace(':round_id', roundId);
  const currentRound = roundList.find((r) => String(r.id) === String(roundId));
  const eventIds = currentRound?.event_ids;

  if (!eventIds?.length) return base;

  const params = new URLSearchParams();
  eventIds.forEach((id) => params.append('tournament_ids[]', id));

  // Portal templates already carry a query string (source=portal)
  return `${base}${base.includes('?') ? '&' : '?'}${params.toString()}`;
};

// A customized summaries-only page carries an explicitly empty event_ids on
// its round entry: "this page selected no events". An absent key means "no
// filter, fetch every event of the round".
const hasEmptyEventSelection = (roundList = [], roundId) => {
  const currentRound = roundList.find((r) => String(r.id) === String(roundId));
  return Array.isArray(currentRound?.event_ids) && currentRound.event_ids.length === 0;
};

// One in-flight tournaments-list fetch at a time: a newer fetchEvents aborts
// the previous one so a slow, out-of-order response can never overwrite
// fresher data (e.g. a quick round switch or breakpoint flapping).
let eventsController = null;

// The view the shell last asked fetchLeaderboard for; the list poller has no
// viewMode of its own and needs it to refill a single view it just emptied.
let lastViewMode = VIEW_MODES.WEB;

// --- Polling state ---
// Single poll chain guarded by object identity (activeEventsPoll !== poll),
// so a chain that resumes after a restart can never duplicate itself.
let activeEventsPoll = null; // { timerId }

// Private: conditional poll using the stored ETag — no loading indicator.
// Returns true when the server sent fresh data, false on 304 (Not Modified),
// an empty list, a stale round, or an error.
const pollEvents = (etag) => async (dispatch, getState) => {
  const { leagueId, leagueName, roundId, roundList, tournamentsUrl } = getState().leaderboardV2;
  const url = buildTournamentsUrl(tournamentsUrl, roundId, roundList);

  try {
    const headers = {};
    if (etag) headers['If-None-Match'] = etag;

    const res = await fetch(url, { headers });

    if (res.status === 304) return false;
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const newEtag = res.headers.get('ETag');
    const data = await res.json();

    dispatchRoundOngoing(dispatch, getState, roundId, data.round_ongoing);

    const eventsList = normalizeEvents(data.tournaments);
    if (Object.keys(eventsList).length === 0) return false;

    // The round may have changed while the request was in flight
    if (getState().leaderboardV2.roundId !== roundId) return false;

    dispatch({
      type: ActionTypes.FETCH_EVENTS_SUCCESS,
      payload: { leagueId, leagueName, eventsList, etag: newEtag, hidePurse: data.hide_purse === true },
    });
    if (data.hide_purse === true) await dispatch(applyHidePurse(lastViewMode));

    return true;
  } catch {
    return false;
  }
};

// Recursive scheduler — adjusts interval based on whether data changed
function scheduleEventsPoll(poll, interval, dispatch, getState) {
  poll.timerId = setTimeout(async () => {
    if (activeEventsPoll !== poll) return;

    const etag = getState().leaderboardV2.eventsListEtag;
    const changed = await dispatch(pollEvents(etag));

    // Re-check after the async fetch — polling may have been restarted/stopped
    if (activeEventsPoll !== poll) return;

    // Legacy stop rule (shouldContinuePolling): a not-ongoing round gets this one
    // poll, then the chain ends - unless the customer's polling optimization is
    // off, in which case polling never stops.
    if (!shouldContinuePolling(getState().leaderboardV2)) {
      activeEventsPoll = null;
      return;
    }

    const nextInterval = nextPollInterval(interval, changed, getState().leaderboardV2);
    scheduleEventsPoll(poll, nextInterval, dispatch, getState);
  }, interval);
}

export const stopEventsPolling = () => {
  if (!activeEventsPoll) return;
  clearTimeout(activeEventsPoll.timerId);
  activeEventsPoll = null;
};

export const startEventsPolling = () => (dispatch, getState) => {
  stopEventsPolling();
  const poll = {};
  activeEventsPoll = poll;
  const { pollMinInterval } = getState().leaderboardV2;
  scheduleEventsPoll(poll, pollMinInterval, dispatch, getState);
};

// The single owner of the "what opens by itself" policy: single-view modes
// (mobile/landscape) and single-event pages open the first event. Anything
// already open wins — an open event is never refetched just to refresh its
// data (reloading the page is the user's refresh). An open summary also
// wins — never auto-open an event next to it (e.g. coming back to web with
// a summary kept from landscape/mobile).
const autoOpenDefaultEvent = (viewMode) => async (dispatch, getState) => {
  const flatEvents = Object.values(getState().leaderboardV2.eventsList ?? {}).flat();
  if (flatEvents.length === 0) return;

  const isSingleView = [VIEW_MODES.MOBILE, VIEW_MODES.LANDSCAPE].includes(viewMode);
  if (!isSingleView && flatEvents.length > 1) return;
  if (Object.keys(getState().event ?? {}).length > 0) return;
  if (selectOpenSummaryKey(getState())) return;

  await dispatch(fetchResultScope(flatEvents[0].id, { viewMode }));
};

// A hide-purse toggle just closed the purse summary a single view was
// showing (closePurseSummaries): web simply loses the tile, but mobile and
// landscape render one thing at a time and would be left blank with no
// picker to recover from. Refill with the first remaining summary, else the
// round's default event.
const refillSingleViewAfterPurseHidden = (viewMode) => async (dispatch, getState) => {
  if (![VIEW_MODES.MOBILE, VIEW_MODES.LANDSCAPE].includes(viewMode)) return;
  if (Object.keys(getState().event ?? {}).length > 0 || selectOpenSummaryKey(getState())) return;

  const firstSummaryKey = selectEnabledSummaryKeys(getState())[0];
  if (firstSummaryKey) {
    await dispatch(fetchSummary(firstSummaryKey));
    return;
  }

  await dispatch(autoOpenDefaultEvent(viewMode));
};

// The list said hide_purse: drop any open purse summary, then keep a single
// view populated. No-op unless a purse summary was actually open.
const applyHidePurse = (viewMode) => async (dispatch) => {
  if (dispatch(closePurseSummaries())) await dispatch(refillSingleViewAfterPurseHidden(viewMode));
};

export const fetchEvents = (viewMode, shouldAutoOpenDefaults = false) => async (dispatch, getState) => {
  const { leagueId, leagueName, roundId, roundList, tournamentsUrl } = getState()?.leaderboardV2;

  // A page whose every round is hidden from the portal boots with no round
  // at all (empty round list, nil initial round): commit the empty state so
  // the empty-state message renders instead of a "null"-round 404, and
  // skip polling — there is no round to poll.
  if (!roundId) {
    dispatch({ type: ActionTypes.FETCH_EVENTS_REQUEST });
    dispatch({
      type: ActionTypes.FETCH_EVENTS_SUCCESS,
      payload: { leagueId, leagueName, eventsList: {}, etag: null },
    });
    return;
  }

  if (hasEmptyEventSelection(roundList, roundId)) {
    // No events will ever load, so summaries own the screen on mobile:
    // mirror the single-event auto-open with the first displayed summary.
    const hasOpenSummary = Boolean(selectOpenSummaryKey(getState()));
    if ([VIEW_MODES.MOBILE, VIEW_MODES.LANDSCAPE].includes(viewMode) && shouldAutoOpenDefaults && !hasOpenSummary) {
      const firstSummaryKey = selectEnabledSummaryKeys(getState())[0];
      if (firstSummaryKey) await dispatch(fetchSummary(firstSummaryKey));
    }
    return;
  }

  const url = buildTournamentsUrl(tournamentsUrl, roundId, roundList);

  eventsController?.abort();
  const controller = new AbortController();
  eventsController = controller;

  dispatch({ type: ActionTypes.FETCH_EVENTS_REQUEST });

  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const etag = res.headers.get('ETag');
    const data = await res.json();

    if (controller.signal.aborted) return;
    eventsController = null;

    dispatchRoundOngoing(dispatch, getState, roundId, data.round_ongoing);

    // An empty round (no tournaments defined yet, or all of them hidden from
    // the portal) is a valid state, not a failure: the events list renders
    // the empty-state message, and polling keeps running so tournaments
    // created mid-round appear without a reload.
    const eventsList = normalizeEvents(data.tournaments);

    dispatch({
      type: ActionTypes.FETCH_EVENTS_SUCCESS,
      payload: { leagueId, leagueName, eventsList, etag, hidePurse: data.hide_purse === true },
    });
    if (data.hide_purse === true) await dispatch(applyHidePurse(viewMode));

    dispatch(startEventsPolling());

    if (shouldAutoOpenDefaults) await dispatch(autoOpenDefaultEvent(viewMode));
  } catch (err) {
    if (err.name === 'AbortError') return;

    dispatch({ type: ActionTypes.FETCH_EVENTS_FAILURE, error: err.message || componentText('shared.request_failed', 'Request failed') });
  }
};

export const fetchPreviewEvents = (roundId) => async (_, getState) => {
  const { roundList, tournamentsUrl } = getState().leaderboardV2;
  const url = buildTournamentsUrl(tournamentsUrl, roundId, roundList);

  snapshotController?.abort();
  const controller = new AbortController();
  snapshotController = controller;

  // Preview state lives in the rounds modal, not Redux, so failures are
  // reported through a discriminated result instead of a failure action:
  // 'aborted' means a newer preview fetch superseded this one (caller ignores
  // it); 'error' lets the modal render an error state instead of stale events.
  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const data = await res.json();
    if (controller.signal.aborted) return { status: 'aborted' };

    snapshotController = null;
    return { status: 'ok', events: normalizeEvents(data.tournaments) };
  } catch (err) {
    return { status: err.name === 'AbortError' ? 'aborted' : 'error' };
  }
};

export const fetchLeaderboard = (viewMode) => async (dispatch, getState) => {
  lastViewMode = viewMode;
  const state = getState();
  const eventsList = state?.leaderboardV2?.eventsList ?? {};
  const eventSliceState = state?.event ?? {};
  const roundId = state?.leaderboardV2?.roundId;
  const roundList = state?.leaderboardV2?.roundList ?? [];

  const hasNoEvents = Object.keys(eventsList).length === 0;

  // CASE 1: fresh loaded page
  if (hasNoEvents) {
    await dispatch(fetchEvents(viewMode, true));
    return;
  }

  // CASE 2: WEB -> LANDSCAPE/MOBILE
  if ([VIEW_MODES.MOBILE, VIEW_MODES.LANDSCAPE].includes(viewMode)) {
    const currentRoundIndex =
      roundList.find((round) => round.id === roundId)?.index ?? null;

    const { eventIdToKeep, openEventId } = findEventToKeep(
      eventsList,
      eventSliceState,
      currentRoundIndex
    );

    // An open event wins: it becomes the mobile view and every summary is
    // dropped.
    if (openEventId) {
      dispatch(clearSummaries());
      dispatch(clearEventSlice(eventIdToKeep));
      return;
    }

    // No open event: the first open summary (display order) becomes the
    // mobile view; the other summaries are dropped and the event slice is
    // cleared too — it should already be empty on this path, this is a
    // safety net.
    const openSummaryKey = selectOpenSummaryKey(state);
    if (openSummaryKey) {
      dispatch(clearSummaries(openSummaryKey));
      dispatch(fullClearEventSlice());
      return;
    }

    await dispatch(fetchResultScope(eventIdToKeep, { viewMode }));
    return;
  }

  // CASE 3: MOBILE -> WEB
  await dispatch(fetchEvents(viewMode, true));
};

export const setRoundId = (roundId) => ({
  type: ActionTypes.SET_ROUND_ID,
  payload: roundId,
});

// Round change from the page-level round selector. Events open in the
// outgoing round are reopened in the new one when they can be matched
// (see matchEventsToReopen); their identities must be captured before the
// event slice is cleared. The details view's round selector has its own
// onRoundChange and never comes through here.
export const changeRound = (newRoundId, viewMode) => async (dispatch, getState) => {
  const previousEventsList = getState().leaderboardV2.eventsList;
  const openEventRecords = collectOpenEventRecords(previousEventsList, getState().event);
  // Summary keys are round-independent — remember the open ones before
  // SET_ROUND_ID wipes the slice, then refetch them for the new round.
  const openSummaryKeys = Object.keys(getState().summaries ?? {});

  dispatch(setRoundId(newRoundId));
  dispatch(fullClearEventSlice());

  await dispatch(fetchEvents(viewMode, false));

  // The user already switched rounds again — reopening now would race the
  // newer changeRound, which owns the screen from here.
  if (String(getState().leaderboardV2.roundId) !== String(newRoundId)) return;

  // An unchanged list means the events fetch failed, was aborted, or never
  // ran (summaries-only page): no event reopens then. Summaries don't depend
  // on the events fetch, so they reopen either way.
  const { eventsList } = getState().leaderboardV2;
  const reopenIds =
    eventsList === previousEventsList ? [] : matchEventsToReopen(openEventRecords, eventsList);

  await Promise.all([
    ...openSummaryKeys.map((key) => dispatch(fetchSummary(key))),
    ...reopenIds.map((id) => dispatch(fetchResultScope(id, { viewMode }))),
  ]);

  if (reopenIds.length === 0 && openSummaryKeys.length === 0) {
    await dispatch(autoOpenDefaultEvent(viewMode));
  }
};

export const commitEvents = (roundId, eventsList) => ({
  type: ActionTypes.COMMIT_EVENTS,
  payload: { roundId, eventsList },
});
